import { Keyboard, KeyboardResize } from '@capacitor/keyboard';

import { isStoreBuild } from './platform';

/**
 * Поведение экранной клавиатуры.
 *
 * ЗАЧЕМ. Это самая заметная на глаз разница между сайтом в браузере и настоящим
 * приложением. В веб-виде клавиатура по умолчанию наезжает на содержимое: поле
 * ввода оказывается под ней, и человек печатает вслепую. Особенно больно в чате
 * и в письменных заданиях IELTS, где поле стоит внизу экрана, а текст длинный.
 *
 * ЧТО ДЕЛАЕМ:
 *   1. Просим систему сжимать веб-вид под клавиатуру, а не накрывать его.
 *      Тогда вёрстка сама пересчитывает высоту, и поле остаётся видимым.
 *   2. Отдаём высоту клавиатуры в CSS-переменную — для экранов с прилипшей
 *      к низу панелью: её нужно поднять, а не оставить под клавиатурой.
 *   3. Прокручиваем к полю, когда клавиатура уже показалась. Браузер делает это
 *      сам, но до изменения высоты, поэтому иногда промахивается.
 *
 * На iPhone скрываем панель «Готово / стрелки» над клавиатурой: она занимает
 * место и в приложении с собственными кнопками выглядит лишней.
 */

export function initKeyboard(): () => void {
  if (!isStoreBuild()) return () => {};

  const removers: Array<() => void> = [];

  // Сжимать веб-вид, а не накрывать его клавиатурой.
  void Keyboard.setResizeMode({ mode: KeyboardResize.Native }).catch(() => {});
  void Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(() => {});

  const setVar = (px: number) => {
    document.documentElement.style.setProperty('--keyboard-height', `${px}px`);
  };

  void Keyboard.addListener('keyboardWillShow', (info) => {
    setVar(info.keyboardHeight);
    document.documentElement.classList.add('keyboard-open');
  }).then((h) => removers.push(() => void h.remove()));

  void Keyboard.addListener('keyboardDidShow', () => {
    // Прокрутка к активному полю — уже после того, как высота изменилась.
    const el = document.activeElement;
    if (el instanceof HTMLElement) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }).then((h) => removers.push(() => void h.remove()));

  void Keyboard.addListener('keyboardWillHide', () => {
    setVar(0);
    document.documentElement.classList.remove('keyboard-open');
  }).then((h) => removers.push(() => void h.remove()));

  return () => removers.forEach((r) => r());
}

/** Закрыть клавиатуру — например, по нажатию вне поля или после отправки. */
export function hideKeyboard(): void {
  if (!isStoreBuild()) return;
  void Keyboard.hide().catch(() => {});
}
